// Precios de productos por lista (filas planas). CRM → Gestión de Precios → Precios.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { BuscadorProducto, Campo, ListaPrecio, Modal, Precio, Producto, REGLA, Switch, fieldCls, inv } from './shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';

const reglaLabel: Record<string, string> = { fijo: 'Precio fijo', descuento_pct: '% desc. s/ base', markup_pct: '% markup s/ costo' };
const vacio = { lista_id: '', producto_id: '', regla: 'fijo', precio: '', porcentaje: '', es_activo: true };

export function Precios() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [filtroLista, setFiltroLista] = useState('');

  const { data: precios = [], isFetching } = useQuery<Precio[]>({
    queryKey: ['inv-precios'], queryFn: () => inv.list<Precio>('precios', '?select=*'),
  });
  const { data: listas = [] } = useQuery<ListaPrecio[]>({
    queryKey: ['inv-listas'], queryFn: () => inv.list<ListaPrecio>('listas_precios', '?select=*&order=nombre'),
  });
  const { data: prods = [] } = useQuery<Producto[]>({
    queryKey: ['inv-productos'], queryFn: () => inv.list<Producto>('productos', '?select=id,codigo,nombre&tiene_variantes=eq.false&order=nombre'),
  });
  const listaName = (id: string) => listas.find((l) => l.id === id)?.nombre ?? '—';
  const prodName = (id: string) => prods.find((p) => p.id === id)?.nombre ?? '—';

  const filtrados = useMemo(
    () => (filtroLista ? precios.filter((p) => p.lista_id === filtroLista) : precios),
    [precios, filtroLista]
  );

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);
  const set = (patch: Partial<typeof vacio>) => setForm((f) => ({ ...f, ...patch }));

  const abrir = (p?: Precio) => {
    if (p) {
      setEditId(p.id);
      setForm({ lista_id: p.lista_id, producto_id: p.producto_id, regla: p.regla, precio: p.precio?.toString() ?? '', porcentaje: p.porcentaje?.toString() ?? '', es_activo: p.es_activo });
    } else { setEditId(null); setForm({ ...vacio, lista_id: filtroLista }); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: async () => {
      const body = {
        lista_id: form.lista_id, producto_id: form.producto_id, regla: form.regla,
        precio: form.regla === 'fijo' ? Number(form.precio) || 0 : null,
        porcentaje: form.regla === 'fijo' ? null : Number(form.porcentaje) || 0,
        es_activo: form.es_activo,
      };
      return editId ? inv.update('precios', editId, body) : inv.create('precios', body);
    },
    onSuccess: () => { toast.success('Precio guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['inv-precios'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => inv.remove('precios', id),
    onSuccess: () => { toast.success('Precio eliminado'); void qc.invalidateQueries({ queryKey: ['inv-precios'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const valorPrecio = (p: Precio) => (p.regla === 'fijo' ? Number(p.precio).toFixed(2) : `${Number(p.porcentaje)}%`);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Precios por lista</h1>
        <div className="flex items-center gap-2">
          <select className={`${fieldCls} w-56`} value={filtroLista} onChange={(e) => setFiltroLista(e.target.value)}>
            <option value="">Todas las listas</option>
            {listas.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
          </select>
          <Button onClick={() => abrir()} disabled={listas.length === 0 || prods.length === 0}><Plus className="w-4 h-4 mr-1" /> Nuevo precio</Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Precios ({filtrados.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          {(listas.length === 0 || prods.length === 0) && (
            <p className="py-4 text-sm text-muted-foreground">Primero crea al menos una lista de precios y un producto.</p>
          )}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Lista</th><th className="py-2 pr-3">Producto</th><th className="py-2 pr-3">Regla</th>
              <th className="py-2 pr-3 text-right">Valor</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filtrados.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Sin precios</td></tr>}
              {filtrados.map((p) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3">{listaName(p.lista_id)}</td>
                  <td className="py-2 pr-3">{prodName(p.producto_id)}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{reglaLabel[p.regla] || p.regla}</td>
                  <td className="py-2 pr-3 text-right font-medium">{valorPrecio(p)}</td>
                  <td className="py-2 pr-3">{p.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(p)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar"
                      onClick={async () => { if (await dlg.confirmar('¿Eliminar este precio?', { peligro: true })) eliminar.mutate(p.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar precio' : 'Nuevo precio'} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Lista *">
              <select className={fieldCls} value={form.lista_id} onChange={(e) => set({ lista_id: e.target.value })}>
                <option value="">— Selecciona —</option>
                {listas.map((l) => <option key={l.id} value={l.id}>{l.nombre} ({l.sentido})</option>)}
              </select>
            </Campo>
            <Campo label="Producto *">
              <BuscadorProducto productos={prods} value={form.producto_id} onChange={(id) => set({ producto_id: id })} />
            </Campo>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Regla">
                <select className={fieldCls} value={form.regla} onChange={(e) => set({ regla: e.target.value })}>
                  {REGLA.map((r) => <option key={r} value={r}>{reglaLabel[r]}</option>)}
                </select>
              </Campo>
              {form.regla === 'fijo'
                ? <Campo label="Precio"><input type="number" step="0.0001" className={fieldCls} value={form.precio} onChange={(e) => set({ precio: e.target.value })} /></Campo>
                : <Campo label="Porcentaje"><input type="number" step="0.001" className={fieldCls} value={form.porcentaje} onChange={(e) => set({ porcentaje: e.target.value })} /></Campo>}
            </div>
            <Switch checked={form.es_activo} onChange={(v) => set({ es_activo: v })} label="Activo" />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.lista_id || !form.producto_id} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
